/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import ImageBlock from '@component/common/block/image-block';
import ProgressBar from '@design-system/progress-bar';
import TextButton from '@design-system/text-button';
import Chip from '@design-system/chip';
import IconTextBlock from '@component/common/block/icon-text-block';
import Gap from '@component/common/gap';
import { useNavigate } from 'react-router-dom';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import type { IClassroomExpressionData, IClassroomExpressionWorkbookData } from '@datatype/iyagigo/api-type';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { CLASSROOM_EXPRESSION_GROUP_NAME } from '@constant/iyagigo/variable';
import otterTestImage from '@asset/image/otter-test.webp';
import { useClassroomExpressionWorkbooksQuery } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';

const BannerContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const TodayStudyContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const ProgressLabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LessonCardsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const CardContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
  width: 100%;
  padding: ${size.MARGIN_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
  cursor: pointer;
`;

const CardContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

const CardTitleContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const ClassroomExpressionHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const studyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const { data: workbooksResponse, isLoading: isWorkbookLoading } = useClassroomExpressionWorkbooksQuery(studyLanguage);
  const workbooks: IClassroomExpressionWorkbookData[] = workbooksResponse?.data ?? [];
  const expressions = workbooks.map((workbook) => workbook.basicExpression);
  const selectedGroupKey =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.currentClassroomExpressionGroupKey) ?? null;
  const expressionGroups = expressions.reduce<
    { groupKey: string; name: string; expressions: IClassroomExpressionData[] }[]
  >((groups, expression) => {
    const group = groups.find((item) => item.groupKey === expression.groupKey);
    if (group) {
      group.expressions.push(expression);
      return groups;
    }

    groups.push({
      groupKey: expression.groupKey,
      name: CLASSROOM_EXPRESSION_GROUP_NAME[expression.groupKey] ?? expression.groupKey,
      expressions: [expression],
    });
    return groups;
  }, []);
  const selectedGroup =
    expressionGroups.find((group) => group.groupKey === selectedGroupKey) ?? expressionGroups[0] ?? null;
  const selectedExpressions = selectedGroup?.expressions ?? [];
  const selectedWorkbooks = workbooks.filter(
    (workbook) => workbook.basicExpression.groupKey === selectedGroup?.groupKey,
  );
  const recommendationWorkbook = selectedWorkbooks.find((workbook) => !workbook.studiedAt);
  const recommendedExpression = recommendationWorkbook?.basicExpression ?? selectedExpressions[0] ?? null;
  const studiedCount = selectedWorkbooks.filter((workbook) => !!workbook.studiedAt).length;
  const totalCount = selectedWorkbooks.length || selectedExpressions.length;
  const isAllStudied = totalCount > 0 && studiedCount === totalCount;

  const handleLessonClick = (lessonKey: string) => {
    navigate(`${IYAGIGO_PATH.CLASSROOM_EXPRESSION_PATH}?language=${studyLanguage.toLowerCase()}&subject=${lessonKey}`);
  };

  const handleGroupSelectClick = () => {
    dispatch(modalActions.showDialogModal({ type: 'classroom-expression-group-select' }));
  };

  const ClassroomExpressionCards = (groupExpressions: IClassroomExpressionData[]) =>
    groupExpressions.map((expression) => {
      const workbook = workbooks.find((item) => item.basicExpression.expressionKey === expression.expressionKey);

      return (
        <CardContainer key={expression.key} onClick={() => handleLessonClick(expression.expressionKey)}>
          <CardContent>
            <CardTitleContainer>
              <TextBlock
                text={expression.expressionName?.ko ?? ''}
                design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
              />
              {!!workbook?.studiedAt && (
                <Chip text='학습 완료' size='tiny' usage='static' kind='outlined' theme={ETheme.GREEN} />
              )}
            </CardTitleContainer>
            <TextBlock
              text={expression.description?.ko ?? ''}
              design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
            />
          </CardContent>
          <IconBlock
            icon='right'
            design={{ icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' }, box: {} }}
          />
        </CardContainer>
      );
    });

  return (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='수달 선생님' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 표현' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock
            text={selectedGroup?.name ?? ''}
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
          />
          <IconTextBlock
            icon='edit'
            text='표현 선택하기'
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{ handler: handleGroupSelectClick }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text='오늘의 학습'
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <TodayStudyContainer>
        <TextBlock
          text={
            isWorkbookLoading
              ? '학습 정보를 불러오고 있어요'
              : isAllStudied
                ? '모든 기본표현을 학습했어요'
                : recommendedExpression
                  ? `<strong>${recommendedExpression.expressionName?.ko ?? '기본표현'}</strong>부터 시작해요`
                  : '기본표현을 시작해 보세요'
          }
          design={{ text: { size: 'large', color: color.BLACK_2 }, box: { alignX: 'left' } }}
        />
        <Gap height='5px' />
        <TextBlock
          text={recommendedExpression?.description?.ko ?? ''}
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
        <Gap height={size.MARGIN_1} />
        <ProgressLabelContainer>
          <TextBlock text='전체 진도' design={{ text: { size: 'small', color: color.BLACK_4, bold: true } }} />
          <TextBlock
            text={`${studiedCount} / ${totalCount}개`}
            design={{ text: { size: 'tiny', color: color.GRAY_1 } }}
          />
        </ProgressLabelContainer>
        <Gap height='5px' />
        <ProgressBar
          ratio={totalCount > 0 ? studiedCount / totalCount : 0}
          theme={ETheme.BROWN}
          width='100%'
          height='6px'
        />
        <Gap height={size.MARGIN_1} />
        <TextButton
          text={isAllStudied ? '다시 학습하기' : '시작하기'}
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          disabled={isWorkbookLoading || !recommendedExpression}
          handleClick={() => {
            if (recommendedExpression) handleLessonClick(recommendedExpression.expressionKey);
          }}
        />
      </TodayStudyContainer>
      <Gap height={size.MARGIN_2} />
      <LessonCardsContainer>{ClassroomExpressionCards(selectedExpressions)}</LessonCardsContainer>
    </>
  );
};

export default ClassroomExpressionHome;
